/**
 * Protected Route component that requires authentication.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner, tokens, makeStyles } from '@fluentui/react-components';
import { useAuth } from '../services/AuthContext';
import { hasAnyRole } from '../utils/roles';

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Single role or array of roles — user must have at least one */
  requiredRole?: string | string[];
  requiredPermission?: string;
  fallbackPath?: string;
}

/**
 * ProtectedRoute component.
 * Redirects to login if not authenticated.
 * Optionally checks for required role or permission.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  fallbackPath = '/login',
}) => {
  const styles = useStyles();
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Check required role(s) — user must have at least one. Case-insensitive: the Entra
  // app registration's `appRoles[].value` casing has drifted across roles added at
  // different times, so a case-sensitive compare here is a latent access-denial bug
  // (see `../utils/roles.ts` and F045-PROGRESS.md owner decision #5).
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!hasAnyRole(user?.roles, roles)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Check required permission
  if (requiredPermission) {
    const hasPermission =
      user?.permissions.includes('*') ||
      user?.permissions.includes(requiredPermission) ||
      user?.permissions.includes(`${requiredPermission.split(':')[0]}:*`);

    if (!hasPermission) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
